import {
  Flex,
  Icon,
  Box,
  Button,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';
import { decode } from 'js-base64';
import { useMemo, useCallback } from 'react';
import { NotificationCard } from '../../components/Card';
import { NotificationTime } from '../../components/Time';
import { NotificationLink } from '../../components/NotificationLink';
import { Mail02 } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components';

const FLOW_OWNER = 'flow_owner';

const MailboxIcon = () => (
  <Box px="12px" pt="8px" pb="10px" borderRadius="8px" bg="pastel-1-light">
    <Icon as={Mail02} color="error-200" w="16px" h="16px" />
  </Box>
);

const ReconnectMailboxContent = ({ notification = {}, onClose = () => {} }) => {
  const { custom_attributes } = notification;

  const {
    url,
    user_type: userType,
    sender_name: senderName,
    affected_flows: affectedFlows = [],
  } = useMemo(() => {
    try {
      if (!custom_attributes) return {};
      return JSON.parse(decode(custom_attributes));
    } catch (err) {
      console.error('Error decoding reconnect mailbox notification', err);
      return {};
    }
  }, [custom_attributes]);

  const handleReauthenticate = useCallback(() => {
    if (url) {
      window.open(url, '_self');
      onClose();
    }
  }, [url, onClose]);

  if (userType === FLOW_OWNER) {
    return (
      <Flex alignItems="start" gap="14px">
        <MailboxIcon />
        <Flex flexDir="column" gap="8px" alignSelf="start" flex="1">
          <KText>
            Kula Flows with <strong>{senderName}</strong> as a message sender
            have stopped. Their email integration has expired and need to be
            re-authenticated. Here are the affected Flows:
          </KText>
          {affectedFlows?.length > 0 && (
            <UnorderedList pl="4px" spacing={1}>
              {affectedFlows.map((flow, i) => {
                let flowPath = flow.url;
                try {
                  flowPath = new URL(flow.url).pathname;
                } catch (err) {
                  console.error('Error parsing flow URL', flow.url, err);
                }
                return (
                  <ListItem key={i}>
                    <NotificationLink
                      to={flowPath}
                      label={flow.title}
                      onClose={onClose}
                      notification={notification}
                    />
                  </ListItem>
                );
              })}
            </UnorderedList>
          )}
          <KText>
            Request them to log in to Kula and reauthenticate their email
            account to ensure your candidates receive the messages on time.
          </KText>
          <NotificationTime notification={notification} />
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex alignItems="start" gap="14px">
      <MailboxIcon />
      <Flex flexDir="column" gap="8px" alignSelf="start" flex="1">
        <KText>
          Your Kula email integration has expired. Reauthenticate to continue
          sending emails from Kula.
        </KText>
        <NotificationTime notification={notification} />
        <Button
          variant="primary"
          size="sm"
          w="fit-content"
          onClick={handleReauthenticate}
          data-test-id="reauthenticate-mailbox-button"
        >
          Reauthenticate
        </Button>
      </Flex>
    </Flex>
  );
};

export const ReconnectMailboxNotification = ({
  notification = {},
  onClose = () => {},
}) => {
  return (
    <NotificationCard notification={notification}>
      <ReconnectMailboxContent notification={notification} onClose={onClose} />
    </NotificationCard>
  );
};
